import React, { useState, useEffect, useCallback } from "react";
import { X, Loader2, Save, AlertCircle, Wand2 } from "lucide-react";
import {
  CAREER_CATEGORIES,
  COMPENSATION_TYPES,
  EMPLOYMENT_TYPES,
  LOCATION_OPTIONS,
  EXPERIENCE_LEVELS,
  getSmartTemplate,
} from "@/data/careerTemplates";

const BLANK = {
  title: "",
  category: "sales",
  location: "Austin, TX",
  compensation_type: "Base + Commission",
  compensation_details: "",
  schedule: "",
  employment_type: "Full-time",
  experience_level: "",
  start_date: "",
  is_active_hiring: true,
  is_featured: false,
  is_published: true,
  is_archived: false,
  overview: "",
  responsibilities: "",
  good_fit_for: "",
  preferred_experience: "",
  compensation_notes: "",
  do_not_promise: "",
  growth_path: "",
};

function LabeledField({ label, htmlFor, children }) {
  return (
    <div>
      <label className="label-br" htmlFor={htmlFor}>{label}</label>
      {children}
    </div>
  );
}

function TextareaBlock({ label, value, onChange, name, rows = 4, placeholder }) {
  return (
    <div>
      <label className="label-br" htmlFor={`field-${name}`}>{label}</label>
      <textarea
        id={`field-${name}`}
        rows={rows}
        className="textarea-br"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={`field-${name}`}
      />
    </div>
  );
}

/**
 * AdminJobListingModal
 *
 * Create/edit modal for a job listing. The “Generate from template” button
 * splats the matching SMART_TEMPLATES copy into the long-form fields based
 * on the selected category, after which the admin can freely edit any
 * field before publishing.
 */
export default function AdminJobListingModal({ open, mode, initial, onClose, onSave }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...BLANK, ...initial } : BLANK);
      setError("");
    }
  }, [open, initial]);

  const update = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value;
    setForm((p) => ({ ...p, [k]: v }));
  };

  const updateField = useCallback((k) => (v) => setForm((p) => ({ ...p, [k]: v })), []);

  const handleGenerateTemplate = () => {
    const tpl = getSmartTemplate(form.category);
    if (!tpl) {
      setError(`No smart template exists yet for the "${form.category}" category. Fill in the description fields manually.`);
      return;
    }
    setError("");
    setForm((p) => ({
      ...p,
      // Only fill the title if the admin hasn't typed one yet.
      title: p.title || tpl.title,
      overview: tpl.overview,
      responsibilities: tpl.responsibilities,
      good_fit_for: tpl.good_fit_for,
      preferred_experience: tpl.preferred_experience,
      compensation_notes: tpl.compensation_notes || "",
      do_not_promise: tpl.do_not_promise,
      growth_path: tpl.growth_path || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError("Title is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave({
        ...form,
        title: form.title.trim(),
      });
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save listing. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[150] bg-br-black/70 backdrop-blur-sm flex items-start justify-center px-3 py-6 sm:py-10 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid="admin-job-listing-modal"
    >
      <div className="w-full max-w-3xl bg-white rounded-lg shadow-2xl my-auto">
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200">
          <div>
            <div className="br-eyebrow text-br-bronze">Career Listing</div>
            <h3 className="font-display text-xl tracking-widest uppercase mt-1">{mode === "edit" ? "Edit Position" : "New Position"}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-gray-500 hover:text-br-black" aria-label="Close" data-testid="job-modal-close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5" data-testid="job-modal-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <LabeledField label="Position Title *" htmlFor="jl-title">
                <input id="jl-title" className="input-br" value={form.title} onChange={update("title")} required data-testid="jl-title" />
              </LabeledField>
            </div>
            <div>
              <LabeledField label="Category *" htmlFor="jl-category">
                <select id="jl-category" className="select-br" value={form.category} onChange={update("category")} required data-testid="jl-category">
                  {CAREER_CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                </select>
              </LabeledField>
            </div>
            <div>
              <LabeledField label="Location" htmlFor="jl-location">
                <select id="jl-location" className="select-br" value={form.location} onChange={update("location")} data-testid="jl-location">
                  {LOCATION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </LabeledField>
            </div>
            <div>
              <LabeledField label="Compensation Type" htmlFor="jl-comp-type">
                <select id="jl-comp-type" className="select-br" value={form.compensation_type} onChange={update("compensation_type")} data-testid="jl-comp-type">
                  {COMPENSATION_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </LabeledField>
            </div>
            <div>
              <LabeledField label="Compensation Details" htmlFor="jl-comp-details">
                <input id="jl-comp-details" className="input-br" value={form.compensation_details} onChange={update("compensation_details")} placeholder="e.g. Base $45k + commission" data-testid="jl-comp-details" />
              </LabeledField>
            </div>
            <div>
              <LabeledField label="Schedule" htmlFor="jl-schedule">
                <input id="jl-schedule" className="input-br" value={form.schedule} onChange={update("schedule")} placeholder="M-F 8-5 / Flexible / etc." data-testid="jl-schedule" />
              </LabeledField>
            </div>
            <div>
              <LabeledField label="Employment Type" htmlFor="jl-employment">
                <select id="jl-employment" className="select-br" value={form.employment_type} onChange={update("employment_type")} data-testid="jl-employment">
                  {EMPLOYMENT_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </LabeledField>
            </div>
            <div>
              <LabeledField label="Experience Level" htmlFor="jl-experience">
                <select id="jl-experience" className="select-br" value={form.experience_level} onChange={update("experience_level")} data-testid="jl-experience">
                  <option value="">Any</option>
                  {EXPERIENCE_LEVELS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </LabeledField>
            </div>
            <div>
              <LabeledField label="Start Date" htmlFor="jl-start">
                <input id="jl-start" className="input-br" value={form.start_date} onChange={update("start_date")} placeholder="e.g. Immediate / 2 weeks" data-testid="jl-start" />
              </LabeledField>
            </div>
          </div>

          {/* Flags */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 border-y border-gray-100 py-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" className="accent-br-bronze h-4 w-4" checked={!!form.is_active_hiring} onChange={update("is_active_hiring")} data-testid="jl-active-hiring" /> Actively hiring
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" className="accent-br-bronze h-4 w-4" checked={!!form.is_featured} onChange={update("is_featured")} data-testid="jl-featured" /> Featured listing
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" className="accent-br-bronze h-4 w-4" checked={!!form.is_published} onChange={update("is_published")} data-testid="jl-published" /> Published
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" className="accent-br-bronze h-4 w-4" checked={!!form.is_archived} onChange={update("is_archived")} data-testid="jl-archived" /> Archived
            </label>
          </div>

          {/* Smart template */}
          <div className="bg-br-paper border border-br-bronze/30 rounded p-3 flex items-start gap-3" data-testid="jl-template-row">
            <Wand2 size={16} className="text-br-bronze mt-1 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm text-br-black">Smart-fill description from category</div>
              <p className="text-xs text-gray-600 mt-0.5">
                Click "Generate from template" to autofill overview, responsibilities, good fit, preferred experience, compensation notes, expectations/guardrails, and growth path based on the selected category. You can then edit any field before publishing.
              </p>
            </div>
            <button
              type="button"
              onClick={handleGenerateTemplate}
              className="br-btn-dark text-[11px] whitespace-nowrap"
              data-testid="jl-generate-template"
            >
              <Wand2 size={12} /> Generate from template
            </button>
          </div>

          {/* Long-form fields */}
          <TextareaBlock label="Position Overview" value={form.overview} onChange={updateField("overview")} name="overview" rows={4} />
          <TextareaBlock label="Responsibilities" value={form.responsibilities} onChange={updateField("responsibilities")} name="responsibilities" rows={6} placeholder="One per line, bullets ok" />
          <TextareaBlock label="Good Fit For" value={form.good_fit_for} onChange={updateField("good_fit_for")} name="good_fit_for" rows={4} placeholder="One per line, bullets ok" />
          <TextareaBlock label="Preferred Experience" value={form.preferred_experience} onChange={updateField("preferred_experience")} name="preferred_experience" rows={3} placeholder="Optional — use for superintendent or specialty roles" />
          <TextareaBlock label="Compensation Notes" value={form.compensation_notes} onChange={updateField("compensation_notes")} name="compensation_notes" rows={3} placeholder="Longer narrative explaining pay structure — admin can edit before publishing." />
          <TextareaBlock label="Expectations / Do Not Promise" value={form.do_not_promise} onChange={updateField("do_not_promise")} name="do_not_promise" rows={4} placeholder="Combines on-the-job expectations + items the team must not promise without approval." />
          <TextareaBlock label="Growth Path" value={form.growth_path} onChange={updateField("growth_path")} name="growth_path" rows={3} placeholder="Optional — how this role can grow inside Black Ridge." />

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 flex items-start gap-2" data-testid="jl-error">
              <AlertCircle size={14} className="mt-0.5 shrink-0" /> {error}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-widest text-gray-500" data-testid="jl-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="br-btn-primary" data-testid="jl-save">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              {saving ? "Saving…" : mode === "edit" ? "Save Changes" : "Create Listing"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
